export type SapsfConfigFormatterField = {
  root?: string[];
  filter?: {
    eq?: {
      and: {
        key: string;
        value: string;
      }[];
    };
    ne?: {
      and: {
        key: string;
        value: string;
      }[];
    };
    exists?: string[];
  };
  type?: string;
  format?: string;
  isShifted?: boolean;
  destination: string | string[];
};

export type SapsfConfig = {
  run: {
    requests: boolean;
    costCenters: boolean;
    subsidiaries: boolean;
  };
  filter: string;
  mapping: {
    pis: string;
    name: string;
    userId: string;
    areaCode: string;
    emailType: string;
    costCenter: string;
    userStatus: string;
    holidayCode: string;
    positionName: string;
    costCenterNav: string;
    departmentName: string;
    subsidiaryCnpj: string;
    subsidiaryName: string;
    phoneNumberType: string;
    pisDocumentType: string;
  };
  formatter: {
    cpf: SapsfConfigFormatterField;
    pis: SapsfConfigFormatterField;
    name: SapsfConfigFormatterField;
    email: SapsfConfigFormatterField;
    phone: SapsfConfigFormatterField;
    endDate: SapsfConfigFormatterField;
    matricula: SapsfConfigFormatterField;
    startDate: SapsfConfigFormatterField;
    externalId: SapsfConfigFormatterField;
    positionName: SapsfConfigFormatterField;
    costCenterCode: SapsfConfigFormatterField;
    costCenterName: SapsfConfigFormatterField;
    departmentName: SapsfConfigFormatterField;
    subsidiaryCnpj: SapsfConfigFormatterField;
    subsidiaryCode: SapsfConfigFormatterField;
    subsidiaryName: SapsfConfigFormatterField;
    scheduleStartDate: SapsfConfigFormatterField;
    positionExternalId: SapsfConfigFormatterField;
    scheduleExternalId: SapsfConfigFormatterField;
    costCenterExternalId: SapsfConfigFormatterField;
    departmentExternalId: SapsfConfigFormatterField;
    subsidiaryExternalId: SapsfConfigFormatterField;
    supervisorExternalId: SapsfConfigFormatterField;
    holidayGroupExternalId: SapsfConfigFormatterField;
    payrollGroupExternalId: SapsfConfigFormatterField;
    businessRulesGroupExternalId: SapsfConfigFormatterField;
  };
  globalInfo: string[];
  shouldSort: boolean;
  currentSyncPath: string;
  expandFieldsOverride: string;
};

export type CsvConfig = {
  run: {
    requests: boolean;
    employees: boolean;
    positions: boolean;
    departments: boolean;
    subsidiaries: boolean;
  };
  separator: string;
  converters: {
    requests: string;
    employees: string;
    positions: string;
    departments: string;
    subsidiaries: string;
  };
  populateSchedules: boolean;
  populateSupervisors: boolean;
  allowEndlessRequests: boolean;
  unchangeableSchedules: boolean;
  filterProcessedRequests: boolean;
};

export type IntegrationConfig = SapsfConfig | CsvConfig | Record<string, unknown>;

type IntegrationConfigSource = {
  integrationConfig?: IntegrationConfig;
  config?: IntegrationConfig;
};

/** Reads integration config from API records (supports legacy `config` key during rollout). */
export function getIntegrationConfig(record: IntegrationConfigSource | Record<string, unknown> | null | undefined): IntegrationConfig | undefined {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const source = record as IntegrationConfigSource;
  return source.integrationConfig ?? source.config;
}

/** Config payload for upsertConfig. */
export function getIntegrationConfigUpsertBody(config: IntegrationConfig | undefined): { integrationConfig: IntegrationConfig } | Record<string, never> {
  if (config === undefined) {
    return {};
  }
  return { integrationConfig: config };
}

/** Normalizes integration records from the API so `integrationConfig` is always populated when config exists. */
export function normalizeIntegrationRecord<T extends { integrationConfig?: IntegrationConfig; config?: IntegrationConfig; uuid?: string; id?: string }>(item: T): T {
  const integrationConfig = getIntegrationConfig(item);
  if (integrationConfig === undefined) {
    return item;
  }
  return { ...item, integrationConfig };
}

export type Integration = {
  uuid: string;
  baseUrl: string;
  vendor: string;
  source: string;
  companyUuid: string;
  otCredentials: {
    clientId: string;
    clientSecret: string;
    companyUuid?: string;
  };
  vendorCredentials: {
    settings: {
      authMethod: string;
      companyId: string;
      grantType: string;
      clientId: string;
      nameIdentifier: string;
      oauthTokenPath: string;
      certificateIssuer: string;
      publicCert: string;
      privateKey: string;
    };
  };
  status: string;
  integrationConfig: IntegrationConfig;
  syncInterval: number;
  lastSyncSuccess: boolean | null;
  lastSyncStartedAt: string | null;
  lastSyncSuccessDate: string | null;
  lastRunDate: string | null;
  minutesToProcess: number | null;
  errorMessage: string | null;
  customState: {
    state: string;
    lastUpdated: string;
  };
  createdAt: string;
  updatedAt: string;
  companyName: string;
  isOtCredentialsValid: boolean;
  id: string;
};
